/**
 * `useCandidatePairings` — TanStack Query for
 * GET /api/t/{slug}/candidates/{id}/jd-pairings.
 *
 * Used by `<CascadePromptModal>` to derive N (in-flight other JDs count).
 * Also used by `<CrossJdModal>` to mark already-paired JDs as disabled.
 *
 * Cache key: `['candidate-pairings', tenantSlug, candidateId]`.
 * staleTime: 15s (pairings change on cross-JD add or status cascade;
 * the mutations invalidate this key on success).
 *
 * Spec: batch_2_5_design.md §Frontend + candidate-detail-rebuild-spec.md §3.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { getJdPairings, type JdPairingRow } from "@/lib/candidatesApi";

export function useCandidatePairings(
  candidateId: string | undefined,
): UseQueryResult<JdPairingRow[], ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useQuery<JdPairingRow[], ApiError>({
    queryKey: ["candidate-pairings", tenantSlug, candidateId],
    queryFn: () => getJdPairings(tenantSlug!, candidateId!),
    enabled: !!tenantSlug && !!candidateId,
    staleTime: 15_000,
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 2;
    },
  });
}
